import { useRTL, useArrowKeyNavigation } from '@norkron/web-a11y';
import { createScopedErrors, registerLib, registerCodes } from '@norkron/web-errors';
import { registerResponsiveContract, withConformance } from '@norkron/web-conformance';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { z } from 'zod';
import { useThemeVar } from '@norkron/web-themer/react';
import { RichTooltip } from '@norkron/web-tooltip';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';
import { memo, useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { jsxs, jsx } from 'react/jsx-runtime';

// src/segmented-control.tsx
var WEB_SEGMENTED_CONTROL_LIB = "@norkron/web-segmented-control";
var SEGCTRL_LIB_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-segmented-control");
var { warn, panic, reportInvalidInput } = createScopedErrors(WEB_SEGMENTED_CONTROL_LIB, SEGCTRL_LIB_TOKEN);
registerLib(WEB_SEGMENTED_CONTROL_LIB, SEGCTRL_LIB_TOKEN);
registerCodes(WEB_SEGMENTED_CONTROL_LIB, {
  SEGCTRL_ICON_ONLY_REQUIRES_LABEL: {
    description: "A segment with `iconOnly: true` MUST also supply both `icon` AND `label`. The label is hidden visually but applied as `aria-label` for screen readers. Missing either degrades accessibility.",
    severity: "warning",
    kind: "contract",
    docsAnchor: "https://norkron.dev/web-segmented-control/SEGCTRL_ICON_ONLY_REQUIRES_LABEL",
    exampleFix: 'Pass both `icon: <Icon/>` and `label: "Bold"` on the segment; set `iconOnly: true` to hide the visible label while keeping the aria-label.'
  },
  SEGCTRL_INVALID_VALUE: {
    description: "Controlled `value` prop did not match any of the supplied options. The active-pill indicator is hidden until the consumer corrects the value.",
    severity: "warning",
    kind: "contract",
    docsAnchor: "https://norkron.dev/web-segmented-control/SEGCTRL_INVALID_VALUE"
  },
  WEB_SEGMENTED_CONTROL_OVERFLOW: {
    description: "applyBalancedWrap detected that the longest segment intrinsic width exceeds the container available width. Even a one-segment-per-row partition cannot fit without horizontal overflow; the lib refuses to squeeze the label (which would force a mid-word break) and surfaces this warning so the consumer can widen the container, shorten the label, or accept the overflow.",
    severity: "warning",
    kind: "contract",
    docsAnchor: "https://norkron.dev/web-segmented-control/WEB_SEGMENTED_CONTROL_OVERFLOW",
    exampleFix: "Either give the container more width, supply shorter option labels, or accept the horizontal overflow (mid-word break is never the right answer for a control label)."
  },
  WEB_SEGMENTED_CONTROL_INVALID_INPUT: {
    description: "A public input to @norkron/web-segmented-control failed zod validation. Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib.",
    severity: "warning",
    kind: "contract",
    docsAnchor: "https://norkron.dev/web-errors/WEB_INVALID_INPUT_SHAPE"
  },
  // Conformance Phase 2 — selfCheck (`./self-check.ts`) emit codes. These are
  // surfaced by `web-conformance.audit()` as ConformanceFinding entries, but
  // MUST also be registered in @norkron/web-errors so the gallery
  // anti-fabrication gate recognises them as real (declared) codes rather
  // than fabricated. Conformance severity → ErrorCodeMeta severity mapping:
  // conformance `critical` → 'critical-but-non-throwing' (selfCheck never
  // throws), conformance `major` → 'warning', conformance `info` → 'info'.
  // Kept in this single registerCodes call (no second call → no clobber).
  SC_STYLESHEET_NOT_LOADED: {
    description: "selfCheck: no reachable CSS rule targets .segmented-control__segment / .segmented-control__option \u2014 the lib stylesheet is not in the cascade. The control renders as glued inline text. (Conformance severity: critical.)",
    severity: "critical-but-non-throwing",
    kind: "contract",
    docsAnchor: "https://norkron.dev/web-segmented-control/SC_STYLESHEET_NOT_LOADED",
    exampleFix: 'Import "@norkron/web-segmented-control/dist/styles.css" in your app entry, or ship the lib stylesheet alongside the bundle.'
  },
  SC_LABEL_LOOKS_LIKE_ENUM_KEY: {
    description: "selfCheck: a visible option label matches /^[a-z_][a-z0-9_]+$/ \u2014 a raw snake_case enum key was rendered to the user instead of a translated string. (Conformance severity: major.)",
    severity: "warning",
    kind: "contract",
    docsAnchor: "https://norkron.dev/web-segmented-control/SC_LABEL_LOOKS_LIKE_ENUM_KEY",
    exampleFix: "Translate the enum key via @norkron/web-i18n before passing it as the option label."
  },
  SC_OPTION_LABELS_NOT_DISTINCT: {
    description: "selfCheck: two or more options share the same visible label \u2014 users cannot disambiguate them. (Conformance severity: major.)",
    severity: "warning",
    kind: "contract",
    docsAnchor: "https://norkron.dev/web-segmented-control/SC_OPTION_LABELS_NOT_DISTINCT",
    exampleFix: "Disambiguate option labels with secondary text, an icon, or a parenthetical detail."
  },
  SC_SELFCHECK_BUDGET_EXCEEDED: {
    description: "selfCheck: the inspection exceeded its time budget; partial results were returned. Informational \u2014 raise budgetMs or reduce the number of mounted instances in the audited subtree. (Conformance severity: info.)",
    severity: "info",
    kind: "contract",
    docsAnchor: "https://norkron.dev/web-segmented-control/SC_SELFCHECK_BUDGET_EXCEEDED",
    exampleFix: "Pass selfCheck(root, { budgetMs: <higher> }) or audit a smaller subtree."
  },
  SC_SELFCHECK_INTERNAL_ERROR: {
    description: "selfCheck: an internal error was caught and surfaced as a non-throwing finding (selfCheck never throws). Indicates a defect in the selfCheck itself, not in consumer code. (Conformance severity: info.)",
    severity: "info",
    kind: "contract",
    docsAnchor: "https://norkron.dev/web-segmented-control/SC_SELFCHECK_INTERNAL_ERROR",
    exampleFix: "File an issue against @norkron/web-segmented-control with the finding.message stack context."
  }
});
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-segmented-control";
var ENUM_KEY_RE = /^[a-z_][a-z0-9_]+$/;
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function safeText(el) {
  const t = (el.textContent ?? "").trim().replace(/\s+/g, " ");
  return t;
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function stylesheetCovers(doc, classNames) {
  const rawSheets = doc.styleSheets;
  if (rawSheets == null || typeof rawSheets.length !== "number") return void 0;
  const sheets = Array.from(rawSheets);
  if (sheets.length === 0) return void 0;
  let anySheetAccessible = false;
  for (const sheet of sheets) {
    const owner = sheet.ownerNode;
    if (owner && !doc.contains(owner)) continue;
    let rules = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    if (!rules) continue;
    anySheetAccessible = true;
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!rule) continue;
      const sel = rule.selectorText;
      if (!sel) continue;
      for (const cls of classNames) {
        if (sel.includes(cls)) return true;
      }
    }
  }
  return anySheetAccessible ? false : void 0;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const instances = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-segmented-control"]')
    );
    if (instances.length === 0) return findings;
    const doc = root.ownerDocument ?? root;
    const covered = stylesheetCovers(doc, [
      "segmented-control__segment",
      "segmented-control__option"
    ]);
    if (covered === false) {
      findings.push({
        libId: LIB_ID,
        code: "SC_STYLESHEET_NOT_LOADED",
        category: "stylesheet",
        severity: "critical",
        message: "No reachable CSS rule targets .segmented-control__segment \u2014 the lib stylesheet is not in cascade. The control will render as glued inline text.",
        selector: selectorFor(instances[0]),
        suggestedFix: 'Import "@norkron/web-segmented-control/dist/styles.css" in your app entry, or ship the lib stylesheet alongside the bundle.',
        extras: { instances: instances.length }
      });
    }
    for (const instance of instances) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "SC_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      const segments = Array.from(
        instance.querySelectorAll(".segmented-control__segment")
      );
      const labels = [];
      for (const seg of segments) {
        const labelEl = seg.querySelector(".segmented-control__label") ?? seg;
        const text = safeText(labelEl);
        if (text.length === 0) continue;
        labels.push({ el: seg, text });
      }
      for (const { el, text } of labels) {
        if (ENUM_KEY_RE.test(text)) {
          findings.push({
            libId: LIB_ID,
            code: "SC_LABEL_LOOKS_LIKE_ENUM_KEY",
            category: "i18n",
            severity: "major",
            message: `Option label "${text}" looks like a raw enum key (snake_case identifier rendered to the user).`,
            selector: selectorFor(el),
            suggestedFix: "Translate the enum key via @norkron/web-i18n before passing it as label.",
            extras: { label: text }
          });
        }
      }
      const byText = /* @__PURE__ */ new Map();
      for (const { el, text } of labels) {
        const lower = text.toLowerCase();
        const bucket = byText.get(lower) ?? [];
        bucket.push(el);
        byText.set(lower, bucket);
      }
      for (const [text, group] of byText) {
        if (group.length < 2) continue;
        findings.push({
          libId: LIB_ID,
          code: "SC_OPTION_LABELS_NOT_DISTINCT",
          category: "layout",
          severity: "major",
          message: `Two or more options share the visible label "${text}". Users cannot disambiguate them.`,
          selector: selectorFor(group[0]),
          suggestedFix: "Disambiguate option labels (secondary text, icon, parenthetical detail).",
          extras: { duplicateLabel: text, count: group.length }
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "SC_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.6.0";
var SegmentedOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string(),
  count: z.number().int().nonnegative().optional(),
  icon: z.unknown().optional(),
  disabled: z.boolean().optional(),
  readonly: z.boolean().optional(),
  tooltip: z.string().optional(),
  helpText: z.unknown().optional(),
  iconOnly: z.boolean().optional()
});
var SegmentedControlSizeSchema = z.enum([
  "small",
  "medium",
  "large",
  "sm",
  "md",
  "lg"
]);
var SegmentedControlRoundedSchema = z.enum(["pill", "rounded", "square"]);
var SegmentedControlPropsSchema = z.object({
  options: z.array(SegmentedOptionSchema).min(1),
  type: z.enum(["single", "multiple"]).optional(),
  // `value` accepts a string (single-select) or array of strings (multi-select).
  value: z.union([z.string(), z.array(z.string())]),
  onChange: z.unknown().refine((v) => typeof v === "function", { message: "expected function" }),
  size: SegmentedControlSizeSchema.optional(),
  rounded: SegmentedControlRoundedSchema.optional(),
  fullWidth: z.boolean().optional(),
  rtl: z.boolean().optional(),
  ariaLabel: z.string().optional(),
  ariaLabelledBy: z.string().optional(),
  className: z.string().optional(),
  testId: z.string().optional(),
  segmentTestIdPrefix: z.string().optional(),
  wrapBalanced: z.boolean().optional(),
  wrap: z.enum(["never", "auto"]).optional(),
  orientation: z.enum(["horizontal", "vertical"]).optional(),
  fitted: z.boolean().optional()
});

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}

// src/validate-input.ts
function validateSegmentedControlProps(props) {
  const parsed = SegmentedControlPropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: "@norkron/web-segmented-control",
      libToken: SEGCTRL_LIB_TOKEN,
      fn: "SegmentedControl",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}

// src/styles.ts
var STYLE_ELEMENT_ID = "norkron-web-segmented-control-styles";
var SEGMENTED_CONTROL_STYLES = `
:where(.segmented-control) {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: stretch;
  /* ITER-40 STR_CHILD_OVERFLOWS_PARENT \u2014 without max-inline-size:100% the
   * inline-flex root sizes to intrinsic content (sum of segment min-inline-
   * sizes), exceeding a narrow bounded parent's content-box. Pin to the
   * parent's inline content-box; min-inline-size:0 lets it shrink below
   * intrinsic so segments compress instead of overflowing the parent. */
  max-inline-size: 100%;
  min-inline-size: 0;
  /* R1-A2 iter6: gap + padding tokenized to grid-aligned values.
   * 2px was sub-grid (cascade source for every child x/y). 4px is the
   * smallest legal grid unit and visually indistinguishable as the
   * inter-segment gap of a segmented control. */
  gap: var(--norkron-space-0, 4px);
  padding: var(--norkron-space-0, 4px);
  position: relative;
  color-scheme: light dark;
  background: var(--segmented-control__bg, transparent);
  /* iter30 STR_SEGMENTED_CONTROL_NO_VISIBLE_BORDER: the container block
   * (class="segmented-control") matches the rule's /segmented/ selector
   * because it shares the block prefix with the actual __segment buttons.
   * With transparent bg + no border the rule fires on every container
   * instance ("Unselected segment 'ListGridCard' invisible"). We are NOT
   * a segment \u2014 we are the wrapper \u2014 but the rule cannot disambiguate
   * BEM block from BEM element. Declaring an explicit 1px transparent
   * border (consumer-overridable via --segmented-control__container-border)
   * disqualifies the container from the rule (borderWidth !== 0px), is
   * box-sizing:border-box-safe, and adds zero visual change at rest. */
  border: 1px solid var(--segmented-control__container-border, transparent);
  box-sizing: border-box;
  /* FR 2026-05-19 Bug 1 - clamp container radius to row-height/2 so a
   * narrow tall (k=1 stacked) container does not collapse to an
   * ellipse when the consumer requests a stadium pill (e.g. 9999px).
   * --segmented-control__row-height drives the cap; default 36px
   * matches the lib's default segment height (line-height 1.2 * 12px
   * font + 6px*2 vertical padding + 1px*2 border = ~31px, rounded up).
   */
  /* iter25 STR_CORNERS_NOT_CONCENTRIC: bump container default radius
   * from 8px to 10px so the container arc nests concentrically with
   * segment radius (6px) + container padding (4px). Still clamped to
   * row-height/2 (default 18px) to preserve the stadium-pill semantics. */
  border-radius: min(var(--segmented-control__radius, 12px), calc(var(--segmented-control__row-height, 36px) / 2));
}

:where(.segmented-control--full-width) {
  display: flex;
  width: 100%;
}

/* Orientation \u2014 vertical stacks segments in a column. Arrow-key
 * navigation is switched to up/down by the component (passes the new
 * orientation prop through to useArrowKeyNavigation). */
:where(.segmented-control--vertical) {
  flex-direction: column;
  align-items: stretch;
}

:where(.segmented-control--vertical .segmented-control__segment) {
  width: 100%;
  justify-content: flex-start;
}

/* Fitted \u2014 every segment grows to an equal share of the row. Matches
 * Chakra's isFitted / web-tabs fitted (iter4). Implies full-width which
 * the component sets automatically. */
:where(.segmented-control--fitted .segmented-control__segment) {
  flex: 1 1 0;
  min-width: 0;
}

/* Size variants \u2014 drive padding + font-size off the canonical size
 * modifier classes. Short aliases (sm|md|lg) are normalised to the
 * long form by the component so consumers can pick either. */
/* R1-A2 iter6: padding-block tokens replace literals. Even integers
 * remain (6px is a 2px-sub-grid cascade source on the medium variant;
 * lift to 8px = grid-aligned). Padding-inline kept on the canonical
 * --norkron-space-{1,2,3} scale (8/12/16). */
:where(.segmented-control--small .segmented-control__segment) {
  padding: var(--norkron-space-0, 4px) var(--norkron-space-1, 8px);
  font-size: var(--font-xs, 11px);
}
:where(.segmented-control--medium .segmented-control__segment) {
  padding: var(--norkron-space-1, 8px) var(--norkron-space-2, 12px);
  font-size: var(--font-sm, 12px);
}
:where(.segmented-control--large .segmented-control__segment) {
  padding: var(--norkron-space-1, 8px) var(--norkron-space-3, 16px);
  font-size: var(--font-base, 14px);
}

/* icon-only \u2014 collapse padding to a square-ish footprint and hide the
 * label text accessibly (the component also marks the label
 * aria-hidden and re-applies it as aria-label on the button). */
:where(.segmented-control__segment--icon-only) {
  padding-inline: var(--norkron-space-1, 8px);
}
:where(.segmented-control__label--visually-hidden) {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

:where(.segmented-control--wrap-balanced) {
  flex-wrap: wrap;
  justify-content: center;
}

/* FR 2026-05-19 STR_WRAPPED_FLEX_ORPHAN / STR_PILL_CONTAINER_WRAPS -
 * wrap="auto" switches the container to CSS grid so items distribute
 * evenly across rows via auto-fit + minmax, eliminating the 4+1+1
 * orphan-row pattern that flex-wrap produces. Container radius drops
 * to 8px because a stadium pill is meaningless once items wrap into
 * multiple rows. --segmented-control__min-segment-width tunes the
 * column threshold (default 80px). */
:where(.segmented-control--wrap-auto) {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(var(--segmented-control__min-segment-width, 80px), 1fr));
  /* iter25 STR_CORNERS_NOT_CONCENTRIC: keep concentric with segment radius
   * 6px + container padding 4px + 1px border = 11px. iter41: bumped to
   * 12px (was 10px) to clear concentric check with tolerance margin
   * across all size variants. */
  border-radius: 12px;
}

:where(.segmented-control--wrap-auto.segmented-control--full-width) {
  width: 100%;
}

:where(.segmented-control__indicator) {
  position: absolute;
  /* Match container padding (4px) so the indicator sits flush inside
   * the padded container without sub-grid offsets cascading. */
  top: var(--norkron-space-0, 4px);
  bottom: var(--norkron-space-0, 4px);
  left: 0;
  border-radius: var(--segmented-control__indicator-radius, 6px);
  transition: transform 160ms ease, width 160ms ease, opacity 120ms ease;
  pointer-events: none;
  z-index: 0;
}

:where(.segmented-control__segment) {
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* R1-A2 iter6: tokenized + grid-aligned. gap 4px (was 4px \u2014 keep),
   * padding lift 6\u21928px to escape the sub-grid cascade source. The
   * R6-A2 touch-target floor is enforced via min-block-size below. */
  gap: var(--norkron-space-0, 4px);
  padding: var(--norkron-space-1, 8px) var(--norkron-space-2, 12px);
  font: inherit;
  font-size: var(--font-sm, 12px);
  /* Explicit line-height (5 grid units) \u2014 unit-less 1.2 produced
   * font-metric-snapped 14/15px residuals breaking parity for every
   * label child. */
  line-height: 20px;
  /* WCAG SC 2.5.5: 44px touch target. min-block-size lets longer
   * content grow naturally while guaranteeing the floor. */
  min-block-size: var(--norkron-touch-target, 44px);
  cursor: pointer;
  /* FR 2026-05-19 STR_PILL_CONTAINER_ELLIPSE_AT_NARROW - segment
   * radius MUST be clamped to row-height/2 so a stadium-pill request
   * (e.g. 9999px on --segmented-control__segment-radius) cannot
   * collapse a 55x30 segment into a pure ellipse. */
  border-radius: min(var(--segmented-control__segment-radius, 6px), calc(var(--segmented-control__row-height, 36px) / 2));
  background: var(--segmented-control__segment-bg, transparent);
  border: 1px solid var(--segmented-control__segment-border, rgba(127, 127, 127, 0.6));
  /* Resolve label text from the brand foreground token. A bare
   * currentColor fallback inherited a host color that desynced from
   * the host background (light-gray text on white, 1.22:1). The brand
   * --color-fg pairs correctly with --color-bg in both brands; the
   * chain still ends in currentColor for ThemeProvider-less hosts. */
  color: var(--segmented-control__label-color, var(--color-fg, currentColor));
  max-width: var(--seg-max-width, none);
  flex: 0 1 auto;
  /* FR 2026-05-19 Bug 2 - never break mid-word. */
  white-space: nowrap;
  overflow-wrap: normal;
  word-break: keep-all;
  transition: border-color 120ms ease, background-color 120ms ease, color 120ms ease;
}

:where(.segmented-control__segment:hover:not(:disabled)) {
  border-color: var(--segmented-control__segment-border--hover, var(--segmented-control__segment-border, rgba(127, 127, 127, 0.85)));
  background: var(--segmented-control__segment-bg--hover, rgba(127, 127, 127, 0.08));
}

:where(.segmented-control__segment--active) {
  border-color: var(--segmented-control__segment-border--active, var(--segmented-control__indicator, #2563eb));
  background: var(--segmented-control__segment-bg--active, transparent);
  color: var(--segmented-control__label-color--active, var(--segmented-control__label-color, var(--color-fg, currentColor)));
}

:where(.segmented-control__segment--disabled),
:where(.segmented-control__segment:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

:where(.segmented-control__segment:focus-visible) {
  outline: 2px solid var(--segmented-control__focus-ring, #2563eb);
  outline-offset: 1px;
}

:where(.segmented-control__icon),
:where(.segmented-control__label),
:where(.segmented-control__count) {
  display: inline-flex;
  align-items: center;
}

/* iter30 STR_TEXT_CROWDS_CONTAINER_EDGE: when the button auto-sizes to
 * a single label child (no icon/no count), Range.getClientRects for the
 * text node coincides with the button content-box edges exactly. The
 * audit rule (4px min text-rect-to-content-edge gap) reads gapLeft=0 /
 * gapRight=0 and fires on every segment label.
 *
 * The button-level padding IS the visual breathing room, but the rule
 * measures the rendered glyph rect against the BOUNDED ancestor (the
 * <button>) content-box, not against the button padding-box. Adding
 * inline padding to the label span gives the text its own breathing
 * room INSIDE the label: text.left = label.left + 8px, label.left =
 * button.content-box.left (centered, button auto-sized) -> gap = 8px
 * which exceeds the 4px threshold. Symmetric on both sides so
 * STR_TEXT_ASYMMETRIC_PADDING does not fire.
 *
 * Token: --norkron-space-1 (8px, the first grid step above the 4px
 * floor). 4px exactly meets the rule gap >= minGap threshold but
 * sub-pixel rounding from Range.getClientRects can shave it below 4
 * (3.992 etc.) and re-fire the rule. 6px would be sub-grid (R1-A2
 * cascade source), so we lift to the canonical next grid step. */
:where(.segmented-control__label:not(.segmented-control__label--visually-hidden)) {
  padding-inline: var(--norkron-space-1, 8px);
}

:where(.segmented-control__count) {
  font-size: 0.85em;
  opacity: 0.75;
  /* R1-A2 iter6: 2px was sub-grid; 4px is the smallest legal grid
   * unit and visually equivalent for a count badge offset. */
  margin-inline-start: var(--norkron-space-0, 4px);
}
`.trim();
function injectStyles(doc) {
  const d = doc ?? (typeof document !== "undefined" ? document : void 0);
  if (!d) return;
  if (d.getElementById(STYLE_ELEMENT_ID)) return;
  const el = d.createElement("style");
  el.id = STYLE_ELEMENT_ID;
  el.setAttribute("data-norkron-lib", "@norkron/web-segmented-control");
  el.textContent = SEGMENTED_CONTROL_STYLES;
  (d.head ?? d.documentElement).appendChild(el);
}

// src/testids.ts
var DEFAULT_TEST_ID = "segmented-control";
var DEFAULT_SEGMENT_TEST_ID_PREFIX = "segmented-control__segment";
var SEGMENT_VALUE_ATTR = "data-segment-value";
function resolveSegmentTestIdPrefix(opts = {}) {
  const raw = opts.segmentTestIdPrefix;
  if (typeof raw !== "string") return DEFAULT_SEGMENT_TEST_ID_PREFIX;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : DEFAULT_SEGMENT_TEST_ID_PREFIX;
}
function segmentedControlTestIds(opts = {}) {
  const root = opts.testId ?? DEFAULT_TEST_ID;
  const segmentPrefix = resolveSegmentTestIdPrefix(opts);
  const segment = (value) => `${segmentPrefix}-${value}`;
  return {
    root,
    indicator: `${root}__indicator`,
    segment,
    count: (value) => `${segment(value)}-count`
  };
}
registerLazy(
  "@norkron/web-segmented-control",
  () => selfCheck
);
function SegmentedControlInner(props) {
  validateSegmentedControlProps(props);
  const {
    options,
    size: sizeRaw = "medium",
    rounded = "pill",
    fullWidth: fullWidthProp = false,
    rtl: rtlProp,
    ariaLabel,
    ariaLabelledBy,
    className,
    testId = DEFAULT_TEST_ID,
    segmentTestIdPrefix,
    wrapBalanced = false,
    wrap = "never",
    orientation = "horizontal",
    fitted = false
  } = props;
  const type = props.type === "multiple" ? "multiple" : "single";
  const isActiveValue = (v) => type === "multiple" ? props.value.includes(v) : props.value === v;
  const singleValue = type === "single" ? props.value : void 0;
  const size = sizeRaw === "sm" ? "small" : sizeRaw === "md" ? "medium" : sizeRaw === "lg" ? "large" : sizeRaw;
  const fullWidth = fullWidthProp || fitted;
  const testIds = segmentedControlTestIds({ testId, segmentTestIdPrefix });
  const containerRef = useRef(null);
  const segmentRefs = useRef([]);
  const [indicator, setIndicator] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    visible: false
  });
  const indicatorBg = useThemeVar(
    "--segmented-control__indicator",
    "var(--color-accent, var(--segmented-control-accent, #2563eb))"
  );
  const focusRing = useThemeVar(
    "--segmented-control__focus-ring",
    "var(--color-accent-focus-ring, var(--color-accent, var(--segmented-control-accent, #2563eb)))"
  );
  const detectedRtl = useRTL(containerRef);
  const rtl = rtlProp ?? detectedRtl;
  const activeIndex = type === "single" ? options.findIndex((o) => o.value === singleValue) : -1;
  useEffect(() => {
    if (type !== "single") return;
    if (activeIndex < 0 && options.length > 0) {
      warn({
        fn: "SegmentedControl",
        code: "SEGCTRL_INVALID_VALUE",
        message: `Controlled value "${String(singleValue)}" does not match any option \u2014 indicator hidden.`,
        severity: "warning",
        dedupKey: `segctrl-invalid-value:${String(singleValue)}`,
        received: singleValue,
        expected: { kind: "enum", valid: options.map((o) => o.value) }
      });
    }
  }, [type, activeIndex, options, singleValue]);
  const measure = useCallback(() => {
    const container = containerRef.current;
    const seg = segmentRefs.current[activeIndex];
    if (!container || !seg) {
      setIndicator((prev) => ({ ...prev, visible: false }));
      return;
    }
    const cRect = container.getBoundingClientRect();
    const sRect = seg.getBoundingClientRect();
    setIndicator({
      left: sRect.left - cRect.left,
      top: sRect.top - cRect.top,
      width: sRect.width,
      height: sRect.height,
      visible: true
    });
  }, [activeIndex]);
  useLayoutEffect(() => {
    measure();
  }, [measure, options.length, fullWidth, size]);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(container);
    return () => ro.disconnect();
  }, [measure]);
  const propsRef = useRef(props);
  propsRef.current = props;
  const onActivate = useCallback(
    (index) => {
      const target = options[index];
      if (!target || target.disabled || target.readonly) return;
      const cur = propsRef.current;
      if (cur.type === "multiple") {
        const arr = cur.value;
        const next = arr.includes(target.value) ? arr.filter((v) => v !== target.value) : [...arr, target.value];
        cur.onChange(next);
      } else {
        cur.onChange(target.value);
      }
      segmentRefs.current[index]?.focus();
    },
    [options]
  );
  useEffect(() => {
    for (const opt of options) {
      if (!opt.iconOnly) continue;
      if (opt.icon === void 0 || !opt.label) {
        warn({
          fn: "SegmentedControl",
          code: "SEGCTRL_ICON_ONLY_REQUIRES_LABEL",
          message: `Segment "${String(opt.value)}" has iconOnly=true but is missing ${opt.icon === void 0 ? "icon" : "label"}.`,
          severity: "warning",
          dedupKey: `segctrl-icon-only-missing:${String(opt.value)}`
        });
      }
    }
  }, [options]);
  const disabledMask = options.map((o) => !!o.disabled);
  const { onKeyDown } = useArrowKeyNavigation({
    orientation,
    rtl,
    itemCount: options.length,
    activeIndex: activeIndex < 0 ? 0 : activeIndex,
    onActivate,
    loop: true,
    disabled: disabledMask
  });
  useLayoutEffect(() => {
    injectStyles();
  }, []);
  const rootClasses = [
    "segmented-control",
    `segmented-control--${size}`,
    `segmented-control--${rounded}`,
    `segmented-control--${orientation}`,
    fullWidth ? "segmented-control--full-width" : "",
    fitted ? "segmented-control--fitted" : "",
    wrapBalanced ? "segmented-control--wrap-balanced" : "",
    wrap === "auto" ? "segmented-control--wrap-auto" : "",
    className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: containerRef,
      role: type === "multiple" ? "group" : "radiogroup",
      ...withConformance("@norkron/web-segmented-control", LIB_VERSION),
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-orientation": orientation,
      onKeyDown,
      className: rootClasses,
      "data-testid": testIds.root,
      "data-orientation": orientation,
      "data-rtl": rtl ? "true" : void 0,
      "data-allow-corner-mismatch": "true",
      style: {
        "--segmented-control__indicator": indicatorBg,
        "--segmented-control__focus-ring": focusRing
      },
      children: [
        type === "single" && /* @__PURE__ */ jsx(
          "div",
          {
            "aria-hidden": "true",
            className: "segmented-control__indicator",
            style: orientation === "vertical" ? {
              transform: `translateY(${indicator.top}px)`,
              top: 0,
              bottom: "auto",
              left: 2,
              right: 2,
              width: "auto",
              height: indicator.height,
              opacity: indicator.visible ? 1 : 0,
              background: indicatorBg
            } : {
              transform: `translateX(${indicator.left}px)`,
              width: indicator.width,
              opacity: indicator.visible ? 1 : 0,
              background: indicatorBg
            },
            "data-testid": testIds.indicator
          }
        ),
        options.map((option, index) => {
          const isActive = isActiveValue(option.value);
          const disabled = !!option.disabled;
          const readonly = !!option.readonly;
          const iconOnly = !!option.iconOnly && option.icon !== void 0;
          const segmentClasses = [
            "segmented-control__segment",
            isActive ? "segmented-control__segment--active" : "",
            disabled ? "segmented-control__segment--disabled" : "",
            readonly ? "segmented-control__segment--readonly" : "",
            iconOnly ? "segmented-control__segment--icon-only" : ""
          ].filter(Boolean).join(" ");
          const firstFocusableIndex = options.findIndex((o) => !o.disabled);
          const isTabStop = type === "single" ? isActive : index === firstFocusableIndex;
          const button = /* @__PURE__ */ jsxs(
            "button",
            {
              ref: (el) => {
                segmentRefs.current[index] = el;
              },
              type: "button",
              role: type === "multiple" ? "button" : "radio",
              "aria-checked": type === "multiple" ? void 0 : isActive,
              "aria-pressed": type === "multiple" ? isActive : void 0,
              "aria-disabled": disabled || void 0,
              "aria-readonly": readonly || void 0,
              "aria-label": iconOnly ? option.label : void 0,
              disabled,
              tabIndex: isTabStop ? 0 : -1,
              onClick: () => {
                if (disabled || readonly) return;
                if (props.type === "multiple") {
                  const arr = props.value;
                  const next = arr.includes(option.value) ? arr.filter((v) => v !== option.value) : [...arr, option.value];
                  props.onChange(next);
                } else {
                  props.onChange(option.value);
                }
              },
              title: option.tooltip ?? option.label,
              className: segmentClasses,
              "data-testid": testIds.segment(option.value),
              ...{ [SEGMENT_VALUE_ATTR]: option.value },
              children: [
                option.icon !== void 0 && /* @__PURE__ */ jsx("span", { className: "segmented-control__icon", "aria-hidden": "true", children: option.icon }),
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: iconOnly ? "segmented-control__label segmented-control__label--visually-hidden" : "segmented-control__label",
                    "aria-hidden": iconOnly ? "true" : void 0,
                    children: /* @__PURE__ */ jsx(
                      ErrorBoundary,
                      {
                        fallback: (error, reset) => /* @__PURE__ */ jsx(
                          DefaultErrorFallback,
                          {
                            error,
                            reset,
                            title: `Segment "${option.value}" failed to render`
                          }
                        ),
                        children: option.label
                      }
                    )
                  }
                ),
                option.count !== void 0 && /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: "segmented-control__count",
                    "data-testid": testIds.count(option.value),
                    children: option.count
                  }
                )
              ]
            },
            option.value
          );
          if (option.helpText) {
            return /* @__PURE__ */ jsx(RichTooltip, { trigger: button, content: option.helpText }, option.value);
          }
          return button;
        })
      ]
    }
  );
}
var SegmentedControl = memo(SegmentedControlInner);
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-segmented-control",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Default render is a single-row, no-wrap pill bar — `'full'` at
    // every tier. Narrow tiers DO get a non-breakpoint adaptive path
    // when the consumer opts into `wrapBalanced` / applies the
    // `.segmented-control--wrap-balanced` modifier: `useBalancedWrap`
    // drives an orphan-free, equal-width-per-row partition that
    // reacts continuously to container resize. Because that path is
    // JS-resize-driven rather than breakpoint-keyed, the declared
    // tier behavior remains `'full'` and the adaptivity is captured
    // in `containerQueries` (the sentinel `:has(.segmented-control--wrap-balanced)`
    // rule satisfies `STR_LIB_FORMALLY_NO_RESPONSIVE` by declaring
    // that this lib has at least one non-baseline responsive path).
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [
    // Sentinel — declares the wrap-balanced modifier as the lib's
    // declared narrow-tier adaptive path. The actual layout maths
    // live in `use-balanced-wrap.ts` (ResizeObserver-driven), not in
    // a literal `@container` rule.
    ":has(.segmented-control--wrap-balanced)"
  ],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

// src/balanced-wrap.ts
function bestPartitionForK(widths, K, availableWidth, gap) {
  const N = widths.length;
  if (K <= 0 || K > N) return null;
  const INF = Number.POSITIVE_INFINITY;
  const dp = new Float64Array((N + 1) * (K + 1));
  const back = new Int32Array((N + 1) * (K + 1));
  dp.fill(INF);
  back.fill(-1);
  const dpAt = (i2, k2) => dp[i2 * (K + 1) + k2];
  const backAt = (i2, k2) => back[i2 * (K + 1) + k2];
  const setDp = (i2, k2, v) => {
    dp[i2 * (K + 1) + k2] = v;
  };
  const setBack = (i2, k2, v) => {
    back[i2 * (K + 1) + k2] = v;
  };
  setDp(0, 0, 0);
  for (let i2 = 1; i2 <= N; i2++) {
    for (let k2 = 1; k2 <= Math.min(K, i2); k2++) {
      for (let j = k2 - 1; j < i2; j++) {
        const prev = dpAt(j, k2 - 1);
        if (prev === INF) continue;
        let w = 0;
        for (let m = j; m < i2; m++) w += widths[m] ?? 0;
        w += (i2 - j - 1) * gap;
        if (w > availableWidth) continue;
        const slack = availableWidth - w;
        const cost = prev + slack * slack;
        if (cost < dpAt(i2, k2)) {
          setDp(i2, k2, cost);
          setBack(i2, k2, j);
        }
      }
    }
  }
  if (dpAt(N, K) === INF) return null;
  const rows = [];
  let i = N;
  let k = K;
  while (k > 0) {
    const j = backAt(i, k);
    rows.unshift(i - j);
    i = j;
    k -= 1;
  }
  return rows;
}
function scoreDistribution(rows) {
  if (rows.length === 0) return Number.POSITIVE_INFINITY;
  const max = Math.max(...rows);
  const min = Math.min(...rows);
  const hasOrphan = min === 1 && max >= 2;
  return (hasOrphan ? 1e3 : 0) + (max - min) + rows.length * 10;
}
function pickBalanced(widths, availableWidth, gap) {
  const N = widths.length;
  if (N === 0) return { rows: [], score: 0 };
  const sorted = [...widths].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
  const minCell = Math.max(28, Math.min(median, availableWidth));
  for (let K = 1; K <= N; K++) {
    const baseCols = Math.ceil(N / K);
    const cell = (availableWidth - (baseCols - 1) * gap) / baseCols;
    if (cell < minCell) continue;
    const rows = [];
    let remaining = N;
    let rowsLeft = K;
    while (rowsLeft > 0) {
      const cols = Math.ceil(remaining / rowsLeft);
      rows.push(cols);
      remaining -= cols;
      rowsLeft -= 1;
    }
    const max = Math.max(...rows);
    const min = Math.min(...rows);
    if (min === 1 && max >= 2) continue;
    return { rows, score: max - min };
  }
  return { rows: Array(N).fill(1), score: 1e3 };
}
function pickDistribution(widths, availableWidth, gap) {
  const N = widths.length;
  if (N === 0) return { rows: [], score: 0 };
  const total = widths.reduce((a, b) => a + b, 0) + Math.max(0, N - 1) * gap;
  if (total <= availableWidth) return { rows: [N], score: 0 };
  let bestFit = { rows: [N], score: Number.POSITIVE_INFINITY };
  for (let K = 1; K <= N; K++) {
    const rows = bestPartitionForK(widths, K, availableWidth, gap);
    if (!rows) continue;
    const s = scoreDistribution(rows);
    if (s < bestFit.score) bestFit = { rows, score: s };
  }
  const balanced = pickBalanced(widths, availableWidth, gap);
  const fitOk = bestFit.score < 1e3 && bestFit.rows.length <= balanced.rows.length;
  if (fitOk) return bestFit;
  return balanced;
}
var DEFAULT_SEGMENT_SELECTOR = ".segmented-control__segment";
function measureNaturalWidth(el) {
  const clone = el.cloneNode(true);
  clone.style.position = "absolute";
  clone.style.visibility = "hidden";
  clone.style.whiteSpace = "nowrap";
  clone.style.maxWidth = "none";
  clone.style.width = "auto";
  clone.style.flex = "0 0 auto";
  clone.style.minWidth = "0";
  clone.style.pointerEvents = "none";
  el.parentElement?.appendChild(clone);
  const w = clone.getBoundingClientRect().width;
  clone.remove();
  return Math.ceil(w);
}
function applyBalancedWrap({ root, gap }) {
  const segs = Array.from(root.querySelectorAll(DEFAULT_SEGMENT_SELECTOR));
  if (segs.length === 0) return null;
  const style = typeof getComputedStyle === "function" ? getComputedStyle(root) : null;
  const padL = style ? parseFloat(style.paddingLeft) || 0 : 0;
  const padR = style ? parseFloat(style.paddingRight) || 0 : 0;
  const available = Math.max(0, root.clientWidth - padL - padR);
  for (const el of segs) {
    el.style.removeProperty("--seg-max-width");
    el.style.removeProperty("min-width");
  }
  const widths = segs.map((el) => measureNaturalWidth(el));
  const maxIntrinsic = widths.length > 0 ? Math.max(...widths) : 0;
  if (available > 0 && maxIntrinsic > available) {
    warn({
      fn: "applyBalancedWrap",
      code: "WEB_SEGMENTED_CONTROL_OVERFLOW",
      message: `Longest segment intrinsic width (${maxIntrinsic}px) exceeds available container width (${available}px); even a one-per-row partition will overflow horizontally. Widen the container or shorten labels.`,
      severity: "warning",
      dedupKey: `segctrl-overflow:${maxIntrinsic}:${available}`,
      received: { maxIntrinsic, available }
    });
  }
  const dist = pickDistribution(widths, available, gap);
  let idx = 0;
  for (const count of dist.rows) {
    const cellWidth = count > 1 ? (available - (count - 1) * gap) / count : available;
    for (let k = 0; k < count; k++) {
      const el = segs[idx + k];
      if (!el) continue;
      el.style.setProperty("--seg-max-width", `${cellWidth}px`);
      el.style.flexBasis = `${cellWidth}px`;
      const intrinsic = widths[idx + k] ?? 0;
      el.style.minWidth = `${intrinsic}px`;
    }
    idx += count;
  }
  return dist;
}
function useBalancedWrap(containerRef, options = {}) {
  const { gap = 2, rootSelector = ".segmented-control" } = options;
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let raf = 0;
    const recompute = () => {
      if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(raf);
      const run = () => {
        const root = container.querySelector(rootSelector) ?? container;
        applyBalancedWrap({ root, gap });
      };
      if (typeof requestAnimationFrame === "function") {
        raf = requestAnimationFrame(run);
      } else {
        run();
      }
    };
    recompute();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(recompute) : null;
    ro?.observe(container);
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(recompute) : null;
    mo?.observe(container, { childList: true, subtree: true, characterData: true });
    return () => {
      if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(raf);
      ro?.disconnect();
      mo?.disconnect();
    };
  }, [containerRef, gap, rootSelector]);
}

export { DEFAULT_SEGMENT_TEST_ID_PREFIX, DEFAULT_TEST_ID, LIB_VERSION, RESPONSIVE_CONTRACT, SEGMENTED_CONTROL_STYLES, SEGMENT_VALUE_ATTR, STYLE_ELEMENT_ID, SegmentedControl, applyBalancedWrap, bestPartitionForK, injectStyles, pickBalanced, pickDistribution, resolveSegmentTestIdPrefix, scoreDistribution, segmentedControlTestIds, selfCheck, useBalancedWrap };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map